import { format } from "date-fns";
import { InvoiceRepository } from "../interfaces";
import { InvoiceRecord, InvoiceStatus } from "../types";
import { InMemoryStore, nextId } from "./store";

export class InMemoryInvoiceRepository implements InvoiceRepository {
  constructor(private store: InMemoryStore) {}

  // Emulates the invoiceCreateHandler lambda: for each client, gathers the
  // un-invoiced services rendered in the range into a numbered invoice and
  // stamps them with its id.
  async create(startDate: Date, endDate: Date, clientIds: string[], label: string): Promise<void> {
    const start = format(startDate, "yyyy-MM-dd");
    const end = format(endDate, "yyyy-MM-dd");

    for (const clientId of clientIds) {
      const rendered = this.store.servicesRendered.filter(
        (s) => s.clientId === clientId && s.date >= start && s.date <= end && !s.invoiceID
      );
      if (rendered.length === 0) continue;

      const subtotal = rendered.reduce((sum, sr) => {
        const service = this.store.services.find((s) => s.id === sr.serviceId);
        return sum + (sr.serviceCost ?? service?.cost ?? 0) * sr.quantity + (sr.mileageCost ?? 0);
      }, 0);
      const dates = rendered.map((sr) => sr.date).sort();

      const invoiceNumber = this.store.settings.invoiceNumber ?? 1;
      this.store.settings.invoiceNumber = invoiceNumber + 1;

      const invoice: InvoiceRecord = {
        id: nextId("invoice"),
        clientID: clientId,
        invoiceName: `${this.store.settings.invoicePrefix ?? "INV"}-${invoiceNumber}`,
        startDate: dates[0],
        endDate: dates[dates.length - 1],
        subtotal,
        label,
        status: InvoiceStatus.CREATED,
        servicesRendered: [],
      };
      this.store.invoices.push(invoice);
      rendered.forEach((sr) => {
        sr.invoiceID = invoice.id;
      });
    }
  }

  async send(invoiceIDs: string[], _message: string, _subject: string): Promise<void> {
    this.store.invoices
      .filter((i) => invoiceIDs.includes(i.id))
      .forEach((i) => {
        i.status = InvoiceStatus.SENT;
      });
  }

  async delete(id: string): Promise<void> {
    this.store.servicesRendered
      .filter((s) => s.invoiceID === id)
      .forEach((s) => {
        s.invoiceID = null;
      });
    this.store.invoices = this.store.invoices.filter((i) => i.id !== id);
  }

  async update(invoice: Partial<InvoiceRecord> & { id: string }): Promise<InvoiceRecord> {
    const idx = this.store.invoices.findIndex((i) => i.id === invoice.id);
    if (idx < 0) throw new Error("Invoice not found to update");
    this.store.invoices[idx] = { ...this.store.invoices[idx], ...invoice };
    return { ...this.store.invoices[idx] };
  }

  async cancel(invoice: InvoiceRecord): Promise<InvoiceRecord> {
    this.store.servicesRendered
      .filter((s) => s.invoiceID === invoice.id)
      .forEach((s) => {
        s.invoiceID = null;
      });
    return this.update(invoice);
  }
}
